import React, { useMemo, useState, useCallback } from "react";
import Modal from "@mui/material/Modal";
import { Box } from "@mui/material";
import { Form, Formik, FormikProps, FormikHelpers, FormikValues } from "formik";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { ThunkDispatch } from "redux-thunk";
import { AnyAction } from "redux";

import { RootState } from "store";
import { closeCreateOrganizationModalAction } from "store/modals/reducer.actions";
import { fetchOrganizationsAction } from "store/oranizations";
import { fetchOrganizationProfileAction } from "store/organizationProfile/reducer.actions";
import apiLibrary from "services/api";
import { Toasts } from "view/components/Toasts";
import usePermissions from "hooks/usePermissions";

import { OrganizationForm } from "./components/OrganizationForm";
import AddAdmin from "./components/AddAdmin";
import { AddCommunity } from "./components/AddCommunity";
import adminRoleCheck from "utils/adminRoleCheck";

// Constants
const MODAL_STYLE: React.CSSProperties = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  borderRadius: "15px",
  backgroundColor: "#ffff",
};

const STEPS = ["Community Details", "Add Admins", "Add Organizations"];
const MAX_MISSION_CHARS = 6000;
const MAX_NAME_CHARS = 255;

// Types
interface OrganizationFormValues {
  communityIds: Array<{ id: number }>;
  missionStatement: string;
  name: string;
  organizationImageId: number | null;
  coverImageId: number | null;
  tagIds: Array<{ value: number }>;
  adminIds: Array<{ id: number }>;
  profileImage: string | null;
  coverImage: string | null;
}

// Validation Schema Factory
const createValidationSchema = (step: number, isCommunityAdmin: boolean) => {
  const baseSchema = {
    missionStatement: Yup.string()
      .nullable()
      .max(MAX_MISSION_CHARS, `${MAX_MISSION_CHARS} Max Characters`),
    name: Yup.string()
      .required("Organization Name is required.")
      .max(
        MAX_NAME_CHARS,
        `Organization Name cannot be more than ${MAX_NAME_CHARS} characters.`
      ),
    organizationImageId: Yup.number().nullable(),
    profileImage: Yup.string().nullable(),
    tagIds: Yup.array(),
    coverImageId: Yup.number().nullable(),
    coverImage: Yup.string().nullable(),
  };

  const communityValidation = isCommunityAdmin
    ? Yup.array()
        .of(
          Yup.object().shape({
            id: Yup.number().required("Community ID is required"),
          })
        )
        .min(1, "At least one community is required")
        .required("Community IDs are required")
    : Yup.array().of(Yup.object().shape({ id: Yup.number() }));

  const adminValidation = isCommunityAdmin
    ? Yup.array()
        .of(
          Yup.object().shape({
            id: Yup.number().required("Admin ID is required"),
          })
        )
        .min(1, "At least one admin is required")
        .required("Admins are required")
    : Yup.array().of(Yup.object().shape({ id: Yup.number() }));

  const schemaByStep: Record<number, any> = {
    1: {
      ...baseSchema,
      communityIds: communityValidation,
      adminIds: Yup.array().of(Yup.object().shape({ id: Yup.number() })),
    },
    2: {
      ...baseSchema,
      communityIds: communityValidation,
      adminIds: adminValidation,
    },
  };

  return Yup.object().shape(
    schemaByStep[step] || {
      ...baseSchema,
      communityIds: Yup.array().of(Yup.object().shape({ id: Yup.number() })),
      adminIds: Yup.array().of(Yup.object().shape({ id: Yup.number() })),
    }
  );
};

// Initial values
const getInitialValues = (): OrganizationFormValues => ({
  communityIds: [],
  missionStatement: "",
  name: "",
  organizationImageId: null,
  coverImageId: null,
  tagIds: [],
  adminIds: [],
  profileImage: null,
  coverImage: null,
});

export const CreateOrganizationModal: React.FC = () => {
  // Redux
  const { createOrganizationModal } = useSelector(
    (state: RootState) => state.modals
  );
  const dispatch: ThunkDispatch<any, any, AnyAction> = useDispatch();

  // Router
  const navigate = useNavigate();
  const { communityId, programId } = useParams<{
    communityId: string;
    programId: string;
  }>();

  // Permissions
  const { organizations } = usePermissions();

  // Local state
  const [activeStep, setActiveStep] = useState(0);
  const [organizationId, setOrganizationId] = useState("");
  const [isLoading] = useState(false);

  // Constants
  const isCommunityAdmin = adminRoleCheck([
    "community_admin",
    "organization_admin",
    "program_admin",
  ]);

  // Computed values
  const currentStep = createOrganizationModal.editMode
    ? createOrganizationModal.data.openAtStep
    : activeStep;

  const validationSchema = useMemo(
    () => createValidationSchema(currentStep, isCommunityAdmin),
    [currentStep, isCommunityAdmin]
  );

  const initialValues = useMemo(
    () =>
      createOrganizationModal.editMode
        ? createOrganizationModal.data
        : getInitialValues(),
    [createOrganizationModal.editMode, createOrganizationModal.data]
  );

  // Navigation helpers
  const goToProfile = useCallback(
    (id: string | number) => {
      dispatch(fetchOrganizationsAction(communityId, programId));
      navigate(`/organizations/${id}/profile`);
      handleClose();
    },
    [dispatch, navigate, communityId, programId]
  );

  const resetToProfileView = useCallback(
    (id: string | number, openStep?: number) => {
      if (openStep === 2) {
        dispatch(fetchOrganizationProfileAction(id));
      } else {
        navigate(`/organizations/${id}/profile`);
      }
      setActiveStep(0);
      handleClose();
    },
    [dispatch, navigate]
  );

  // API calls
  const updateOrganization = useCallback(
    async (id: string | number, values: any) => {
      const response =
        await apiLibrary.OrganizationDirectory.updateOrganization(id, values);
      return response.message;
    },
    []
  );

  const createOrganization = useCallback(async (values: any) => {
    const response =
      await apiLibrary.OrganizationDirectory.createOrgaznization(values);
    return { id: response.data.id, message: response.message };
  }, []);

  const addAdminsToOrganization = useCallback(
    async (orgId: string, adminIds: string[]) => {
      const { message } =
        await apiLibrary.OrganizationDirectory.addAdminsToOrganization(
          createOrganizationModal.editMode
            ? createOrganizationModal.data.id
            : orgId,
          adminIds
        );
      return message;
    },
    [createOrganizationModal.editMode, createOrganizationModal.data?.id]
  );

  // Modal handlers
  const handleClose = useCallback(() => {
    dispatch(closeCreateOrganizationModalAction());
    setActiveStep(0);
  }, [dispatch]);

  const backToPreviousPage = useCallback(() => {
    if (activeStep > 0) {
      setActiveStep((prev) => prev - 1);
    }
  }, [activeStep]);

  // Form submission
  const prepareFormValues = useCallback(
    (values: any) => ({
      ...values,
      communityIds: values.communityIds?.map((c: any) => c.id) ?? [],
      tagIds: values.tagIds?.map((t: any) => t.value) ?? [],
      adminIds: values.adminIds?.map((a: any) => a.id) ?? [],
    }),
    []
  );

  const submitForm = useCallback(
    async (values: any, { setSubmitting }: FormikHelpers<any>) => {
      try {
        const { data, editMode } = createOrganizationModal;
        const hasCommunities = values.communityIds?.length > 0;
        const shouldAddAdmins = organizations.canCreateAdmins;
        const preparedValues = prepareFormValues(values);

        let successMessage = "";

        // Edit mode - update existing organization
        if (editMode && data.openAtStep !== 2) {
          successMessage = await updateOrganization(data.id, preparedValues);
          dispatch(fetchOrganizationProfileAction(data.id));
          handleClose();

          // Update existing org at step 1
        } else if (organizationId && activeStep === 1) {
          successMessage = await updateOrganization(
            organizationId,
            preparedValues
          );
          shouldAddAdmins
            ? setActiveStep((prev) => prev + 1)
            : goToProfile(organizationId);

          // Move to admin step without creating org yet
        } else if (activeStep === 1 && data.openAtStep !== 2) {
          setActiveStep((prev) => prev + 1);

          // Create org + add admins at step 2 (if org not yet created)
        } else if (activeStep === 2 && shouldAddAdmins && !organizationId) {
          const { id, message } = await createOrganization(preparedValues);
          setOrganizationId(id);
          successMessage = message;

          successMessage = successMessage;
          resetToProfileView(id, data.openAtStep);

          // Add admins at step 2
        } else if (activeStep === 2 && shouldAddAdmins && organizationId) {
          successMessage = await addAdminsToOrganization(
            organizationId,
            values.adminIds.map((a: any) => a.id)
          );
          resetToProfileView(organizationId, data.openAtStep);

          // Add admins with forced open step
        } else if (data.openAtStep && shouldAddAdmins) {
          successMessage = await addAdminsToOrganization(
            data.id,
            values.adminIds.map((a: any) => a.id)
          );
          resetToProfileView(data.id, data.openAtStep);

          // Fallback
        } else {
          resetToProfileView(organizationId);
        }

        if (successMessage) {
          Toasts.success(successMessage);
        }
      } catch (error: any) {
        const errorMsg = error?.response?.data?.message ?? error.message;
        Toasts.error(errorMsg);
        console.error("[CreateOrganization] Error:", error);
      } finally {
        setSubmitting(false);
      }
    },
    [
      createOrganizationModal,
      organizations.canCreateAdmins,
      organizationId,
      activeStep,
      prepareFormValues,
      updateOrganization,
      createOrganization,
      addAdminsToOrganization,
      goToProfile,
      resetToProfileView,
      handleClose,
      dispatch,
    ]
  );

  const handleSubmit = useCallback(
    (values: any, actions: FormikHelpers<any>) => {
      if (activeStep === 0 && !createOrganizationModal.editMode) {
        setActiveStep((prev) => prev + 1);
      } else {
        submitForm(values, actions);
      }
    },
    [activeStep, createOrganizationModal.editMode, submitForm]
  );

  // Render step content
  const renderStepContent = useCallback(
    (step: number, formikProps: FormikProps<any>) => {
      const commonProps = {
        handleClose,
        activeStep,
        isFormLoading: isLoading,
        isLastStep: false,
      };

      switch (step) {
        case 0:
          return <OrganizationForm {...formikProps} {...commonProps} />;
        case 1:
          return (
            <AddCommunity
              {...formikProps}
              {...commonProps}
              backToPreviousPage={backToPreviousPage}
            />
          );
        case 2:
          return (
            <AddAdmin
              {...formikProps}
              {...commonProps}
              organizationId={organizationId}
              backToPreviousPage={backToPreviousPage}
            />
          );
        default:
          return <div>Not Found</div>;
      }
    },
    [activeStep, isLoading, organizationId, handleClose, backToPreviousPage]
  );

  return (
    <Modal
      open={createOrganizationModal.isOpen}
      onClose={handleClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
      disableAutoFocus
      className="border-none"
    >
      <Box sx={MODAL_STYLE} className="dark:bg-secondaryLight">
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          validateOnChange={false}
          enableReinitialize
        >
          {(formikProps) => (
            <Form>
              <div className="flex flex-col justify-start items-start w-[700px] rounded-lg">
                <div
                  className="relative flex flex-col items-start self-stretch justify-start flex-grow-0 flex-shrink-0 gap-4 rounded-lg bg-bgWhite dark:bg-secondaryLight"
                  style={{
                    boxShadow:
                      "0px 2px 8px 0 rgba(2,13,36,0.14), 0px 2px 24px 0 rgba(2,13,36,0.08)",
                  }}
                >
                  {renderStepContent(currentStep, formikProps)}
                </div>
              </div>
            </Form>
          )}
        </Formik>
      </Box>
    </Modal>
  );
};
